/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `App` for the Caintra Connect frontend.
 * @module App
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { Routes, Route } from "react-router";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { NavigationProvider } from "./context/NavigationContext";

import Login from "./pages/Login";
import SSOCallback from "./pages/SSOCallback";
import Payment3DSCallback from "./pages/Payment3DSCallback";
import Register from "./pages/Register.jsx";
import Home from "./pages/Home";
import Directory from "./pages/Directory";
import Users from "./pages/Users";
import UserRoles from "./pages/UserRoles";
import CompanyCatalog from "./pages/CompanyCatalog";
import CompanyEdit from "./pages/CompanyEdit";
import CompanyProfile from "./pages/CompanyProfile";
import CategoriesCatalog from "./pages/CategoriesCatalog";
import Requirements from "./pages/Requirements";
import PasswordRecovery from "./pages/PasswordRecovery";
import PasswordSuccess from "./pages/PasswordSuccess";
import ChangePasswordView from "./pages/ChangePasswordView.jsx";
import MainLayout from "./components/MainLayout";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Chat from "./pages/Chat";
import Events from "./pages/Events";
import EventPreRegister from "./pages/EventPreRegister";
import ManageEvents from "./pages/ManageEvents";
import EventCreation from "./pages/EventCreation";
import BusinessEvents from "./pages/BusinessEvents";
import BusinessEventForm from "./pages/BusinessEventForm";
import BusinessEventDetails from "./pages/BusinessEventDetails";
import B2BEvents from "./pages/B2BEvents";
import B2BEventForm from "./pages/B2BEventForm";
import B2BEventDetails from "./pages/B2BEventDetails";
import B2BEventVirtualLineCompanies from "./pages/B2BEventVirtualLineCompanies";
import VirtualEvents from "./pages/VirtualEvents";
import VirtualEventForm from "./pages/VirtualEventForm";
import BreakfastEvents from "./pages/BreakfastEvents";
import BreakfastEventForm from "./pages/BreakfastEventForm";
import BreakfastEventInviteesList from "./pages/BreakfastEventInviteesList";
import ViewAppointments from "./pages/ViewAppointments";
import AppointmentRequest from "./pages/AppointmentRequest";
import Attendance from "./pages/Attendance";
import B2BEventUsersList from "./pages/B2BEventUsersList";
import BuyerCompanies from "./pages/BuyerCompanies";
import BuyerCompanyForm from "./pages/BuyerCompanyForm";
import Reports from "./pages/Reports";
import Help from "./pages/Help";
import NotificationCenter from "./pages/NotificationCenter";
import NotificationForm from "./pages/NotificationForm";
import Unsubscribe from "./pages/Unsubscribe";
import "./App.css";

function App() {
  return (
    <NavigationProvider>
      <div className="App">
        {/* Download failures from the asset proxy toast into this container. */}
        <ToastContainer containerId="asset-downloads" />
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/sso/callback" element={<SSOCallback />} />
          <Route
            path="/payment/3ds-callback"
            element={<Payment3DSCallback />}
          />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/passwordRecovery" element={<PasswordRecovery />} />
          <Route path="/changePassword" element={<ChangePasswordView />} />
          <Route path="/passwordSuccess" element={<PasswordSuccess />} />
          <Route path="/termsAndConditions" element={<TermsAndConditions />} />
          <Route path="/privacyPolicy" element={<PrivacyPolicy />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route element={<MainLayout />}>
            <Route path="/home" element={<Home />} />
            <Route path="/companyCatalog" element={<CompanyCatalog />} />
            <Route path="/companyEdit/:companyId" element={<CompanyEdit />} />
            <Route path="/categoryCatalog" element={<CategoriesCatalog />} />
            <Route path="/directory" element={<Directory />} />
            <Route path="/companyProfile" element={<CompanyProfile />} />
            <Route path="/users" element={<Users />} />
            <Route path="/userRoles" element={<UserRoles />} />
            <Route path="/requirements" element={<Requirements />} />
            <Route path="/events" element={<Events />} />
            <Route
              path="/events/:eventId/pre-register"
              element={<EventPreRegister />}
            />
            <Route path="/manageEvents" element={<ManageEvents />} />
            <Route path="/addEvent" element={<EventCreation />} />
            <Route path="/eventEdit/:eventId" element={<EventCreation />} />
            <Route path="/attendance" element={<Attendance />} />
            <Route path="/b2bEventUsersList" element={<B2BEventUsersList />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/buyerCompanies" element={<BuyerCompanies />} />
            <Route path="/buyerCompanyForm" element={<BuyerCompanyForm />} />
            <Route path="/businessEvents" element={<BusinessEvents />} />
            <Route
              path="/businessEvent/:eventId"
              element={<BusinessEventDetails />}
            />
            <Route path="/businessEventForm" element={<BusinessEventForm />} />
            <Route path="/b2bEvents" element={<B2BEvents />} />
            <Route path="/b2bEventForm" element={<B2BEventForm />} />
            <Route
              path="/b2bEvent/:eventId/virtual-line-companies"
              element={<B2BEventVirtualLineCompanies />}
            />
            <Route path="/b2bEvent/:eventId" element={<B2BEventDetails />} />
            <Route path="/virtualEvents" element={<VirtualEvents />} />
            <Route path="/virtualEventForm" element={<VirtualEventForm />} />
            <Route path="/breakfastEvents" element={<BreakfastEvents />} />
            <Route
              path="/breakfastEventForm"
              element={<BreakfastEventForm />}
            />
            <Route
              path="/breakfastEventInviteesList/:eventId"
              element={<BreakfastEventInviteesList />}
            />
            <Route path="/viewAppointments" element={<ViewAppointments />} />
            <Route
              path="/appointmentRequest"
              element={<AppointmentRequest />}
            />
            <Route path="/help" element={<Help />} />
            <Route path="/reports" element={<Reports />} />
            <Route
              path="/notificationCenter"
              element={<NotificationCenter />}
            />
            <Route path="/notificationForm" element={<NotificationForm />} />
          </Route>
        </Routes>
      </div>
    </NavigationProvider>
  );
}

export default App;
